import { useDigitalJourney } from '@cannect/hooks/useDigitalJourney'
import useSignedUser from '@cannect/hooks/useSignedUser'
import { DIGITAL_JOURNEY_ROUTES, FORM_DEFAULT_VALUES } from './constants'
import { Button, Typography } from '@cannect/new-components/atoms'
import { useConfirmJourneyPaymentStepMutation } from '@cannect/services/resources/digitalJourney'
import { useFindLastJourney } from '@cannect/services/resources/digitalJourney'
import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { DIGITAL_JOURNEY_PAGALEVE_STORAGE_KEY } from './Steps/OrderPaymentStep/useOrderPaymentStep'

type PagaleveReturnState = 'processing' | 'error'

export const DigitalJourneyPagaleveReturn: React.FC = () => {
  const history = useHistory()
  const location = useLocation()
  const { signedUser } = useSignedUser()
  const { continueJourney, updateFormData } = useDigitalJourney()
  const [state, setState] = useState<PagaleveReturnState>('processing')
  const [errorMessage, setErrorMessage] = useState<string>('')

  const query = useMemo(() => new URLSearchParams(location.search), [location.search])
  const statusParam = query.get('status')
  const reasonParam = query.get('reason') || 'canceled'
  const orderIdParam = query.get('order_id')

  const confirmJourneyPaymentStepMutation = useConfirmJourneyPaymentStepMutation()
  const { data: lastJourneyData } = useFindLastJourney(signedUser?.id)

  useEffect(() => {
    const storedRaw = localStorage.getItem(DIGITAL_JOURNEY_PAGALEVE_STORAGE_KEY)
    const storedContext = storedRaw ? JSON.parse(storedRaw) : null

    const redirectToPayment = (reason: string) => {
      localStorage.removeItem(DIGITAL_JOURNEY_PAGALEVE_STORAGE_KEY)
      const search = new URLSearchParams({ pagaleve_error: reason }).toString()
      history.replace(`${DIGITAL_JOURNEY_ROUTES.order_payment}?${search}`)
    }

    if (!storedContext?.digital_journey_id) {
      setState('error')
      setErrorMessage('Não conseguimos confirmar sua jornada. Tente voltar para o passo de pagamento e escolher outra forma.')
      return
    }

    // success flow
    if (statusParam === 'paid') {
      confirmJourneyPaymentStepMutation
        .mutateAsync({ digital_journey_id: storedContext.digital_journey_id })
        .then((response) => {
          localStorage.removeItem(DIGITAL_JOURNEY_PAGALEVE_STORAGE_KEY)

          if (response?.form_data) {
            updateFormData({
              ...response.form_data,
              common_fields: {
                ...(response.form_data?.common_fields || FORM_DEFAULT_VALUES.common_fields),
                digital_journey_id: storedContext.digital_journey_id,
                order_id: storedContext.order_id ?? Number(orderIdParam) ?? null,
                status: response?.status || 'paid'
              }
            })
          } else {
            updateFormData({
              common_fields: {
                ...FORM_DEFAULT_VALUES.common_fields,
                digital_journey_id: storedContext.digital_journey_id,
                order_id: storedContext.order_id ?? Number(orderIdParam) ?? null,
                status: 'paid'
              }
            })
          }

          if (lastJourneyData) {
            continueJourney(lastJourneyData)
            return
          }

          history.replace(response?.next_step || DIGITAL_JOURNEY_ROUTES.generate_and_sign_anvisa_authorization)
        })
        .catch(() => {
          setState('error')
          setErrorMessage('Não conseguimos confirmar seu pagamento no momento. Tente novamente dentro da jornada.')
        })
      return
    }

    // canceled / error flow
    redirectToPayment(reasonParam)
  }, [
    confirmJourneyPaymentStepMutation,
    continueJourney,
    history,
    lastJourneyData,
    orderIdParam,
    statusParam,
    reasonParam,
    updateFormData
  ])

  if (state === 'error') {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <Typography.Heading type="headingSix" className="text-primary-700">
          Algo não saiu como esperado
        </Typography.Heading>
        <Typography.Text type="paragraphOne" className="max-w-lg text-neutral-600">
          {errorMessage || 'Não conseguimos validar o retorno do Pagaleve. Volte ao passo de pagamento e tente novamente.'}
        </Typography.Text>
        <Button onClick={() => history.replace(DIGITAL_JOURNEY_ROUTES.order_payment)}>Voltar ao pagamento</Button>
      </div>
    )
  }

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 px-4 text-center">
      <Typography.Heading type="headingSix" className="text-primary-700">
        Estamos confirmando seu pagamento...
      </Typography.Heading>
      <Typography.Text type="paragraphOne" className="max-w-lg text-neutral-600">
        Assim que recebermos o retorno do Pagaleve, você continuará automaticamente a sua jornada.
      </Typography.Text>
    </div>
  )
}

